import { useState, useEffect, useCallback, useRef } from "react";
import { X, Search, Users, MessageCircle, Check, Loader2 } from "lucide-react";
import {
  searchTeamUsers,
  getOrCreateDM,
  createGroup,
} from "../../../../api/teamChat";

/**
 * NewConversationModal — Unified search for creating DMs and Groups.
 *
 * Select 1 person → "Message [Name]" button (creates DM)
 * Select 2+ people → group name input + "Create Group" button
 *
 * Props:
 *   onClose: () => void
 *   onConversationCreated: (conversation, type) => void
 */
const NewConversationModal = ({ onClose, onConversationCreated }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const searchInputRef = useRef(null);
  const debounceRef = useRef(null);

  const isGroup = selectedUsers.length >= 2;

  // Focus search on mount
  useEffect(() => {
    setTimeout(() => searchInputRef.current?.focus(), 100);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await searchTeamUsers(query.trim());
        setResults(res?.data?.users || []);
      } catch (e) {
        console.error("Search failed:", e);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [query]);

  /* ── Toggle user selection ── */
  const toggleUser = useCallback((user) => {
    setSelectedUsers((prev) => {
      const exists = prev.find((u) => u.uuid === user.uuid);
      if (exists) return prev.filter((u) => u.uuid !== user.uuid);
      return [...prev, user];
    });
    setError("");
  }, []);

  const isSelected = useCallback(
    (uuid) => selectedUsers.some((u) => u.uuid === uuid),
    [selectedUsers],
  );

  /* ── Create conversation ── */
  const handleCreate = useCallback(async () => {
    if (selectedUsers.length === 0) return;

    setCreating(true);
    setError("");

    try {
      if (selectedUsers.length === 1) {
        // DM
        const res = await getOrCreateDM(selectedUsers[0].uuid);
        const conv = res?.data?.conversation;
        if (conv) {
          onConversationCreated?.(conv, "dm");
          onClose();
        } else {
          setError("Failed to create conversation");
        }
      } else {
        // Group
        const name =
          groupName.trim() ||
          selectedUsers.map((u) => u.display_name || u.first_name).join(", ");
        const memberUuids = selectedUsers.map((u) => u.uuid);
        const res = await createGroup(name, memberUuids);
        const conv = res?.data?.conversation;
        if (conv) {
          onConversationCreated?.(conv, "group");
          onClose();
        } else {
          setError("Failed to create group");
        }
      }
    } catch (e) {
      console.error("Failed to create conversation:", e);
      setError(e?.response?.data?.error || "Something went wrong");
    } finally {
      setCreating(false);
    }
  }, [selectedUsers, groupName, onClose, onConversationCreated]);

  /* ── Escape to close ── */
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="w-[420px] max-h-[520px] flex flex-col bg-white dark:bg-zinc-900 rounded-xl shadow-2xl border border-zinc-200 dark:border-zinc-700 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 dark:border-zinc-800">
        <h3 className="text-[14px] font-semibold text-zinc-800 dark:text-zinc-200">
          New Conversation
        </h3>
        <button
          onClick={onClose}
          className="p-1 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
        >
          <X className="h-4 w-4 text-zinc-500" strokeWidth={2} />
        </button>
      </div>

      {/* Selected users chips */}
      {selectedUsers.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-4 pt-3">
          {selectedUsers.map((u) => (
            <span
              key={u.uuid}
              className="inline-flex items-center gap-x-1 px-2 py-0.5 rounded-full bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 text-[11px] font-medium"
            >
              {u.display_name || u.first_name}
              <button
                onClick={() => toggleUser(u)}
                className="hover:text-sky-900 dark:hover:text-sky-100"
              >
                <X className="h-3 w-3" strokeWidth={2} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Group name input — only when 2+ selected */}
      {isGroup && (
        <div className="px-4 pt-2">
          <input
            type="text"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="Group name (optional)"
            className="w-full px-3 py-1.5 rounded-md border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 text-[12px] text-zinc-800 dark:text-zinc-200 placeholder-zinc-400 dark:placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-sky-500"
          />
        </div>
      )}

      {/* Search input */}
      <div className="px-4 pt-3 pb-2">
        <div className="relative">
          <Search
            className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400"
            strokeWidth={2}
          />
          <input
            ref={searchInputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search people..."
            className="w-full pl-8 pr-3 py-2 rounded-md border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 text-[13px] text-zinc-800 dark:text-zinc-200 placeholder-zinc-400 dark:placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-sky-500"
          />
          {searching && (
            <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400 animate-spin" />
          )}
        </div>
      </div>

      {/* Results list */}
      <div className="flex-1 overflow-y-auto px-2 pb-2 min-h-0">
        {results.length === 0 && query.trim() && !searching && (
          <p className="px-2 py-4 text-center text-[12px] text-zinc-400 dark:text-zinc-600 italic">
            No users found
          </p>
        )}
        {results.length === 0 && !query.trim() && (
          <p className="px-2 py-4 text-center text-[12px] text-zinc-400 dark:text-zinc-600 italic">
            Search for people to message
          </p>
        )}
        {results.map((user) => {
          const displayName = user.display_name || user.first_name || "Unknown";
          const initial = (displayName[0] || "?").toUpperCase();
          const selected = isSelected(user.uuid);

          return (
            <button
              key={user.uuid}
              onClick={() => toggleUser(user)}
              disabled={creating}
              className={[
                "w-full flex items-center gap-x-3 px-3 py-2 rounded-lg text-left transition-colors",
                selected
                  ? "bg-sky-50 dark:bg-sky-900/20"
                  : "hover:bg-zinc-50 dark:hover:bg-zinc-800/40",
                creating ? "opacity-50 cursor-not-allowed" : "",
              ].join(" ")}
            >
              <span className="w-8 h-8 rounded-full bg-gradient-to-br from-zinc-300 to-zinc-400 dark:from-zinc-600 dark:to-zinc-700 flex items-center justify-center text-[13px] font-medium text-white shrink-0">
                {initial}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-zinc-800 dark:text-zinc-200 truncate">
                  {displayName}
                </p>
                {user.email && (
                  <p className="text-[11px] text-zinc-400 dark:text-zinc-600 truncate">
                    {user.email}
                  </p>
                )}
              </div>
              <span
                className={[
                  "w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors",
                  selected
                    ? "bg-sky-500 border-sky-500"
                    : "border-zinc-300 dark:border-zinc-600",
                ].join(" ")}
              >
                {selected && (
                  <Check className="h-3 w-3 text-white" strokeWidth={3} />
                )}
              </span>
            </button>
          );
        })}
      </div>

      {/* Error */}
      {error && (
        <div className="px-4 py-2">
          <p className="text-[11px] text-red-500">{error}</p>
        </div>
      )}

      {/* Action button — adapts to selection count */}
      {selectedUsers.length > 0 && (
        <div className="px-4 py-3 border-t border-zinc-200 dark:border-zinc-800">
          <button
            onClick={handleCreate}
            disabled={creating}
            className="w-full flex items-center justify-center gap-x-2 py-2 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {creating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isGroup ? (
              <Users className="h-4 w-4" strokeWidth={2} />
            ) : (
              <MessageCircle className="h-4 w-4" strokeWidth={2} />
            )}
            {isGroup
              ? `Create Group (${selectedUsers.length} members)`
              : `Message ${selectedUsers[0].display_name || selectedUsers[0].first_name}`}
          </button>
        </div>
      )}
    </div>
  );
};

export default NewConversationModal;
